import MoreVertIcon from '@mui/icons-material/MoreVert'
import Menu from '@mui/material/Menu'
import IconButton from '@mui/material/IconButton'
import { ReactNode, useState } from 'react'
import { Link as ReactRouterLink } from 'react-router'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import { styles } from '../../../Common'
import { Theme } from '@mui/material/styles'
import { default as MuiLink } from '@mui/material/Link'

interface ActionMenuLink extends ActionMenuOption {
  href?: string
  openInNewTab?: boolean
}

interface ActionMenuButton extends ActionMenuOption {
  onClick?: () => void
}

interface ActionMenuOption {
  icon: ReactNode
  label: string
  isDestructive?: boolean
  'data-test-id'?: string
}

interface ActionMenuProps {
  options: (ActionMenuLink | ActionMenuButton)[]
  'data-test-id'?: string
}

const deleteStyle = (theme: Theme) => ({ ...styles.error(theme), '&:hover': styles.error })

export const ActionMenu = (props: ActionMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(null)
  }

  const renderOptionContent = (option: ActionMenuLink | ActionMenuButton) => {
    const { icon, label } = option
    return (
      <>
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText primary={label} />
      </>
    )
  }

  return (
    <>
      <IconButton
        aria-label="open action menu"
        data-test-id={props['data-test-id'] ? `open-${props['data-test-id']}` : 'open-action-menu'}
        aria-haspopup="true"
        onClick={handleClick}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        data-test-id={props['data-test-id'] || 'action-menu'}
        data-testid={props['data-test-id'] || 'action-menu'}
        data-is-open={open ? 'true' : 'false'}
        keepMounted
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {props.options.map((option, index) => {
          // Tries to use the most "native" way to open a link

          if ('onClick' in option) {
            const { onClick } = option as ActionMenuButton
            return (
              <MenuItem
                key={index}
                sx={option.isDestructive ? deleteStyle : undefined}
                data-test-id={option['data-test-id']}
                data-testid={option['data-test-id']}
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                  onClick && onClick()
                  handleClose(e)
                }}
              >
                {renderOptionContent(option)}
              </MenuItem>
            )
          }

          if (!('href' in option)) return null
          const { href, openInNewTab } = option as ActionMenuLink
          if (!href) return null

          if (!openInNewTab) {
            return (
              <MenuItem
                key={index}
                sx={option.isDestructive ? deleteStyle : undefined}
                data-test-id={option['data-test-id']}
                data-testid={option['data-test-id']}
                component={ReactRouterLink}
                to={href}
              >
                {renderOptionContent(option)}
              </MenuItem>
            )
          }

          return (
            <MenuItem
              key={index}
              sx={option.isDestructive ? deleteStyle : undefined}
              data-test-id={option['data-test-id']}
              data-testid={option['data-test-id']}
              component={MuiLink}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {renderOptionContent(option)}
            </MenuItem>
          )
        })}
      </Menu>
    </>
  )
}
